import useSelector from '@hooks/useSelector'

import { generateUrls } from '@hooks/useArtwork'

import type { EntitiesState } from '@redux/entities'
import type { ArtworkModel, CampaignQuoteItemModel, OrderItemModel } from '@models/types'

// The reason for doing this as a function is so that
// we can use it outside of a render function when hooks
// are not supported and just pass the callbacks around

type GetRelationsParams = {
  artwork: ArtworkModel,
  entities: EntitiesState,
}

const getRelations = (params: GetRelationsParams) => {
  const { artwork, entities } = params

  const { campaign_id } = artwork || {}

  const { agencies, campaigns } = entities

  const campaign = campaigns[campaign_id] || {}
  const agency = agencies[campaign.agency_id] || {}

  return {
    agency,
    campaign,
  }
}

type BuildCreateArtworkUrlParams = {
  campaignQuoteItem?: Partial<CampaignQuoteItemModel>,
  orderItem?: Partial<OrderItemModel>,
  redirectUrl?: string,
}

export const buildCreateArtworkUrl = (params: BuildCreateArtworkUrlParams) => {
  const { campaignQuoteItem, orderItem, redirectUrl } = params

  if (!campaignQuoteItem && !orderItem){
    return null
  }

  const { createArtworkUrl } = generateUrls({
    campaign_quote_item_id: campaignQuoteItem?.id,
    order_item_id: orderItem?.id,
  }, { includeRedirect: true, redirectUrl })

  return createArtworkUrl
}

type BuildEditArtworkUrlParams = {
  artwork: ArtworkModel,
  redirectUrl?: string,
}

export const buildEditArtworkUrl = (params: BuildEditArtworkUrlParams) => {
  const { artwork, redirectUrl } = params

  if (!artwork){
    return null
  }

  const { editArtworkUrl } = generateUrls(artwork, { includeRedirect: true, redirectUrl })

  return editArtworkUrl
}

type UseArtworkBuilderPathsOptions = {
  artwork: ArtworkModel,
  campaignQuoteItem?: CampaignQuoteItemModel,
  orderItem?: OrderItemModel,
}

function useArtworkBuilderPaths(params: UseArtworkBuilderPathsOptions) {
  const { artwork, campaignQuoteItem, orderItem } = params

  const entities = useSelector(reduxState => reduxState.entities)

  const relations = getRelations({ artwork, entities })
  const { agency, campaign } = relations

  return {
    agency,
    callbacks: {
      buildCreateArtworkUrl,
      buildEditArtworkUrl,
    },
    campaign,
    createArtworkUrl: buildCreateArtworkUrl({
      campaignQuoteItem,
      orderItem,
    }),
    editArtworkUrl: buildEditArtworkUrl({ artwork }),
  }
}

export default useArtworkBuilderPaths
